import type { PermissionCode } from '@/app/types/PermissionCode';
import { PERMISSION } from '@/app/permissions';
import { holdsPermission } from '@/app/holdsPermission';
import type { InstitutionType } from '@/features/admin/institutions/types/InstitutionType';

/** The three registers this screen administers, in the order the tabs show them. */
export const INSTITUTION_TYPE = {
  training: 'TRAINING',
  serviceProvider: 'SERVICE_PROVIDER',
  research: 'RESEARCH',
} as const;

/**
 * What each register is gated on. One code per type, and the list endpoint demands all three at
 * once when `type` is absent — which is why this screen always sends a type, a tab at a time,
 * rather than asking for everything and filtering what comes back.
 */
export const INSTITUTION_PERMISSION: Record<InstitutionType, PermissionCode> = {
  TRAINING: PERMISSION.trainingInstitutionsManageAny,
  SERVICE_PROVIDER: PERMISSION.consultancyInstitutionsManageAny,
  RESEARCH: PERMISSION.researchOrgsManageAny,
};

/** The types the screen is reachable for at all — any one of the three is enough. */
export const INSTITUTION_PERMISSIONS: readonly PermissionCode[] =
  Object.values(INSTITUTION_PERMISSION);

/**
 * The tabs this session may open.
 *
 * Only the registers it administers: a tab it cannot read would answer 403, and offering it
 * promises a list that will never arrive. MAIA_ADMIN holds all three; a narrower role holds one,
 * and then the screen is that one register rather than a row of dead tabs.
 */
export function institutionTabs(permissions: ReadonlySet<string>): InstitutionType[] {
  return Object.values(INSTITUTION_TYPE).filter((type) =>
    holdsPermission(permissions, INSTITUTION_PERMISSION[type]),
  );
}
